/**
 * Frontend Telemetry Service
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description Application Insights telemetry for frontend error tracking
 * @implements FR-020, FR-021, FR-022, FR-023, FR-024
 * @author Peter Dobler
 * @created 2026-01-20
 */

import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ErrorCodes, ErrorCode, ErrorSeverity, FrontendErrorContext } from '../../../shared/types/errors';

/**
 * Telemetry event names
 */
export const TelemetryEvents = {
  ERROR_BOUNDARY_TRIGGERED: 'ErrorBoundaryTriggered',
  API_ERROR: 'ApiError',
  NETWORK_ERROR: 'NetworkError',
  UNHANDLED_ERROR: 'UnhandledError',
  USER_ACTION: 'UserAction',
} as const;

/**
 * Telemetry configuration
 */
export interface TelemetryConfig {
  instrumentationKey?: string;
  connectionString?: string;
  environment?: 'dev' | 'uat' | 'prod';
  enableAutoRouteTracking?: boolean;
  enableCorsCorrelation?: boolean;
  samplingPercentage?: number;
}

/**
 * Offline event queue item
 */
interface QueuedEvent {
  type: 'exception' | 'event' | 'metric';
  data: any;
  timestamp: string;
}

// Maximum offline queue size (FR-068)
const MAX_OFFLINE_QUEUE_SIZE = 100;
const OFFLINE_QUEUE_KEY = 'fcp_telemetry_queue';

// Application Insights instance
let appInsights: ApplicationInsights | null = null;
let isInitialized = false;

// Session correlation ID from auth
let sessionCorrelationId: string | undefined;

// Capacity context
let capacityContext: { capacityId?: string; capacityType?: string; clientId?: string } = {};

/**
 * Initialize Application Insights SDK
 * FR-060: Initialize Application Insights SDK
 */
export function initializeTelemetry(config: TelemetryConfig): void {
  if (isInitialized) {
    console.warn('[TelemetryService] Already initialized');
    return;
  }

  // Skip in test environment or when no key provided
  if (
    (!config.instrumentationKey && !config.connectionString) ||
    config.instrumentationKey === 'test' ||
    config.connectionString === 'test'
  ) {
    console.log('[TelemetryService] Skipping initialization (no key)');
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: config.instrumentationKey,
        connectionString: config.connectionString,
        enableAutoRouteTracking: config.enableAutoRouteTracking ?? true,
        enableCorsCorrelation: config.enableCorsCorrelation ?? true,
        samplingPercentage: config.samplingPercentage ?? 100,
        disableExceptionTracking: false,
        disableTelemetry: false,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
      },
    });

    appInsights.loadAppInsights();

    // FR-065: Add browser info enrichment
    appInsights.addTelemetryInitializer((envelope) => {
      if (envelope.data) {
        // Add browser context
        (envelope.data as any).userAgent = navigator.userAgent;
        (envelope.data as any).browser = getBrowserInfo();
        (envelope.data as any).os = getOSInfo();
        
        // Add session correlation
        if (sessionCorrelationId) {
          (envelope.data as any).sessionCorrelationId = sessionCorrelationId;
        }

        // FR-064: Add capacity context when available
        if (capacityContext.capacityId) {
          (envelope.data as any).capacityId = capacityContext.capacityId;
          (envelope.data as any).capacityType = capacityContext.capacityType;
        }

        if (capacityContext.clientId) {
          (envelope.data as any).clientId = capacityContext.clientId;
        }

        if (config.environment) {
          (envelope.data as any).environment = config.environment;
        }
      }
      return true;
    });

    isInitialized = true;
    console.log('[TelemetryService] Initialized successfully');

    // FR-067: Flush offline queue on initialization
    flushOfflineQueue();
  } catch (error) {
    console.error('[TelemetryService] Failed to initialize:', error);
  }
}

/**
 * Track exception with error context enrichment
 * FR-061: trackException method with error context enrichment
 */
export function trackException(
  error: Error,
  context?: Partial<FrontendErrorContext>
): void {
  const enrichedContext = {
    ...context,
    timestamp: new Date().toISOString(),
    route: window.location.pathname,
    userAgent: navigator.userAgent,
    ...capacityContext,
  };

  if (appInsights && isInitialized) {
    // Convert to string-only record for App Insights
    const properties = Object.entries(enrichedContext).reduce((acc, [key, value]) => {
      acc[key] = value === undefined || value === null ? '' : String(value);
      return acc;
    }, {} as Record<string, string>);

    appInsights.trackException({
      exception: error,
      severityLevel: mapSeverityToAppInsights(context?.severity),
      properties,
    });
  } else {
    // FR-066: Queue for offline
    queueEvent({
      type: 'exception',
      data: { error: error.message, stack: error.stack, context: enrichedContext },
      timestamp: new Date().toISOString(),
    });
  }

  // Always log to console for debugging
  console.error('[TelemetryService] Exception tracked:', {
    error: error.message,
    context: enrichedContext,
  });
}

/**
 * Track custom event
 * FR-062: trackEvent method for custom events
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string | number | boolean>
): void {
  const enrichedProperties = {
    ...properties,
    timestamp: new Date().toISOString(),
    route: window.location.pathname,
    ...capacityContext,
  };

  if (appInsights && isInitialized) {
    appInsights.trackEvent({
      name,
      properties: enrichedProperties as Record<string, string>,
    });
  } else {
    queueEvent({
      type: 'event',
      data: { name, properties: enrichedProperties },
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * Track metric
 */
export function trackMetric(
  name: string,
  value: number,
  properties?: Record<string, string>
): void {
  if (appInsights && isInitialized) {
    appInsights.trackMetric({
      name,
      average: value,
      sampleCount: 1,
    }, properties);
  } else {
    queueEvent({
      type: 'metric',
      data: { name, value, properties },
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * Set session correlation ID
 * FR-063: Add session correlation ID from auth module
 */
export function setSessionCorrelationId(id: string): void {
  sessionCorrelationId = id;
  
  if (appInsights && isInitialized) {
    appInsights.setAuthenticatedUserContext(id);
  }
}

/**
 * Set capacity context
 * FR-064: Add capacity context when available
 */
export function setCapacityContext(context: { capacityId?: string; capacityType?: string; clientId?: string }): void {
  capacityContext = context;
}

/**
 * Flush telemetry
 */
export function flush(): void {
  if (appInsights && isInitialized) {
    appInsights.flush();
  }
}

/**
 * Track error from ErrorBoundary
 * FR-069: Integrate telemetry with ErrorBoundary
 */
export function trackErrorBoundaryError(
  error: Error,
  componentName: string,
  errorInfo?: { componentStack?: string }
): void {
  trackException(error, {
    code: ErrorCodes.COMPONENT_ERROR,
    message: error.message,
    component: componentName,
    severity: ErrorSeverity.HIGH,
  });

  trackEvent(TelemetryEvents.ERROR_BOUNDARY_TRIGGERED, {
    component: componentName,
    errorMessage: error.message,
    hasComponentStack: !!errorInfo?.componentStack,
  });
}

/**
 * Track API error
 * FR-070: Integrate telemetry with API interceptor
 */
export function trackApiError(
  statusCode: number,
  errorCode: string,
  message: string,
  correlationId?: string,
  referenceId?: string
): void {
  trackEvent(TelemetryEvents.API_ERROR, {
    statusCode,
    errorCode,
    message,
    correlationId: correlationId || '',
    referenceId: referenceId || '',
  });

  // FR-111: Sample high-volume non-critical errors
  if (statusCode < 500 && Math.random() > 0.1) {
    // Only log 10% of non-critical API errors
    return;
  }

  trackException(new Error(`API Error: ${message}`), {
    code: errorCode as ErrorCode,
    message,
    severity: statusCode >= 500 ? ErrorSeverity.CRITICAL : ErrorSeverity.MEDIUM,
  });
}

// Helper functions

function mapSeverityToAppInsights(severity?: ErrorSeverity): SeverityLevel {
  switch (severity) {
    case ErrorSeverity.CRITICAL:
      return SeverityLevel.Critical;
    case ErrorSeverity.HIGH:
      return SeverityLevel.Error;
    case ErrorSeverity.MEDIUM:
      return SeverityLevel.Warning;
    case ErrorSeverity.LOW:
      return SeverityLevel.Information;
    default:
      return SeverityLevel.Error;
  }
}

function getBrowserInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getOSInfo(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS')) return 'iOS';
  return 'Unknown';
}

/**
 * Queue event for offline storage
 * FR-066: Implement offline queue with localStorage fallback
 */
function queueEvent(event: QueuedEvent): void {
  try {
    const queue = getOfflineQueue();
    
    // FR-068: Cap at 100 events
    if (queue.length >= MAX_OFFLINE_QUEUE_SIZE) {
      queue.shift(); // Remove oldest
    }
    
    queue.push(event);
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.warn('[TelemetryService] Failed to queue event:', error);
  }
}

function getOfflineQueue(): QueuedEvent[] {
  try {
    const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Flush offline queue
 * FR-067: Add queue flush on reconnection
 */
function flushOfflineQueue(): void {
  if (!appInsights || !isInitialized) return;

  const queue = getOfflineQueue();
  if (queue.length === 0) return;

  console.log(`[TelemetryService] Flushing ${queue.length} queued events`);

  for (const event of queue) {
    switch (event.type) {
      case 'exception':
        appInsights.trackException({
          exception: new Error(event.data.error),
          properties: event.data.context,
        });
        break;
      case 'event':
        appInsights.trackEvent({
          name: event.data.name,
          properties: event.data.properties,
        });
        break;
      case 'metric':
        appInsights.trackMetric({
          name: event.data.name,
          average: event.data.value,
        }, event.data.properties);
        break;
    }
  }

  // Clear queue
  localStorage.removeItem(OFFLINE_QUEUE_KEY);
}

// Listen for online event to flush queue
if (typeof window !== 'undefined') {
  window.addEventListener('online', flushOfflineQueue);
}

export default {
  initializeTelemetry,
  trackException,
  trackEvent,
  trackMetric,
  setSessionCorrelationId,
  setCapacityContext,
  flush,
  trackErrorBoundaryError,
  trackApiError,
};
